import cx from 'classnames';
import PropTypes from 'prop-types';

import { CustomTooltip } from 'src/components/CustomTooltip';
import { ToneOfVoiceCard } from 'src/components/ToneOfVoiceCard';
import { alignChannelFromBackend, CHANNEL, getChannelPerformanceLabel } from 'src/data/channel';
import { isNonEnglish } from 'src/data/generation';
import { GENERATION_TOOL, getBackendResourcesFromTool } from 'src/data/generationTool';
import { EMAILS_CHANNELS_AND_RESOURCES, getResourceDocumentLabel } from 'src/data/performance';
import { formatNumber } from 'src/utils/format';

import { useStyles } from './useStyles';

const getCustomModelDescription = (customModel) => {
  const channel = alignChannelFromBackend(customModel.dataSource);
  return `${
    getChannelPerformanceLabel(channel) || getResourceDocumentLabel(channel)
  }\nUnique copy assets: ${formatNumber(customModel.usedCopies || customModel.dataPointsSize)}`;
};

const getDisabledHint = (dataSource, isEnglish) => {
  if (!isEnglish) {
    return 'This custom model is only available in English.';
  }

  const channel = EMAILS_CHANNELS_AND_RESOURCES.includes(alignChannelFromBackend(dataSource))
    ? CHANNEL.emailChannel
    : alignChannelFromBackend(dataSource);
  return `This custom model only works with the ${getChannelPerformanceLabel(channel)} template.`;
};

export const CustomModelCard = ({
  className,
  customModel,
  generationTool,
  onClick,
  selected,
  disabled,
  language,
}) => {
  const styles = useStyles();

  const description = getCustomModelDescription(customModel);
  const dataSource = customModel.dataSource;
  const toolDataSources = getBackendResourcesFromTool(generationTool);
  const notToolDataSource = dataSource && !toolDataSources.includes(dataSource);
  const isEnglish = !isNonEnglish(language);
  const isDisabled = disabled || notToolDataSource || !isEnglish;

  return (
    <CustomTooltip
      classes={{ tooltip: styles.tooltip }}
      title={isDisabled ? getDisabledHint(dataSource, isEnglish) : ''}
    >
      <span>
        <ToneOfVoiceCard
          className={cx(styles.root, className)}
          classes={{
            description: styles.description,
          }}
          title={customModel.accountName}
          description={description}
          label={customModel.brandVoiceName}
          tag="Custom model"
          onClick={onClick}
          selected={selected}
          disabled={isDisabled}
        />
      </span>
    </CustomTooltip>
  );
};

CustomModelCard.propTypes = {
  className: PropTypes.string,
  customModel: PropTypes.shape({
    dataSource: PropTypes.string,
    accountName: PropTypes.string,
    brandVoiceName: PropTypes.string,
    usedCopies: PropTypes.number,
    dataPointsSize: PropTypes.number,
  }).isRequired,
  generationTool: PropTypes.oneOf(Object.values(GENERATION_TOOL)),
  onClick: PropTypes.func.isRequired,
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  language: PropTypes.string,
};
CustomModelCard.defaultProps = {
  className: null,
  generationTool: null,
  selected: false,
  disabled: false,
  language: null,
};
